import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于小宝探花',
    description: '了解小宝探花的品牌故事、创作理念和联系方式。小宝探花致力于为用户提供高质量原创视频内容。',
    url: 'https://npjyf.cn/about',
    about: {
      '@type': 'Organization',
      name: '小宝探花',
      description: '探花达人带你玩转精彩内容',
    },
  };

  const formJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系我们 - 小宝探花',
    url: 'https://npjyf.cn/about',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value || '';
    if (honeypot.trim()) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9tfqh78l97n3eas3970', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的连接后重试');
      setFormStatus('error');
    }
  };

  const stats = [
    { value: '500+', label: '原创视频', icon: 'ri-vidicon-line' },
    { value: '100万+', label: '粉丝关注', icon: 'ri-heart-line' },
    { value: '3年', label: '创作历程', icon: 'ri-time-line' },
    { value: '50+', label: '合作品牌', icon: 'ri-shake-hands-line' },
  ];

  const milestones = [
    { year: '2023', title: '小宝探花创立', desc: '怀揣对内容创作的热爱，小宝探花正式上线，开启了探花达人的精彩旅程。' },
    { year: '2024', title: '快速成长突破', desc: '凭借独特的内容风格和真诚的创作态度，粉丝数突破50万，成为垂直领域的新锐力量。' },
    { year: '2025', title: '多元内容扩展', desc: '内容矩阵不断丰富，从户外探险延伸到日常Vlog、美食探店、音乐创作等多个领域。' },
    { year: '2026', title: '全新升级出发', desc: '品牌全面升级，推出全新官方网站，为粉丝提供更优质的内容体验和互动服务。' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(formJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-14">
          {/* Breadcrumb */}
          <div className="px-4 md:px-6 mb-6 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Intro Hero */}
          <section className="px-4 md:px-6 py-10 md:py-14 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20elegant%20abstract%20background%20with%20soft%20golden%20gradients%2C%20minimalist%20texture%2C%20warm%20amber%20and%20cream%20tones%2C%20sophisticated%20atmospheric%20lighting%2C%20gentle%20bokeh%20effect&width=1920&height=500&seq=about-hero-01&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30"></div>
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-background-50 mb-4">
                关于小宝探花
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-xl mx-auto leading-relaxed">
                小宝探花是一个以探花达人为核心的内容创作平台，致力于为用户提供高质量、有温度的原创视频内容。
                我们相信，好的内容可以连接人心、传递美好。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {stats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className="text-center p-6 rounded-lg bg-background-100"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-10">
                品牌历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
                <div className="flex flex-col gap-8">
                  {milestones.map((item, i) => (
                    <div
                      key={item.year}
                      className={`flex items-start gap-6 ${
                        i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        <div className="bg-background-50 rounded-lg p-5">
                          <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-600">{item.desc}</p>
                        </div>
                      </div>
                      <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-bold text-sm">
                        {i + 1}
                      </div>
                      <div className="flex-1 md:hidden">
                        <div className="bg-background-50 rounded-lg p-4">
                          <span className="text-xs text-primary-500 font-semibold">{item.year}</span>
                          <h3 className="text-base font-semibold font-heading text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-xs text-foreground-600">{item.desc}</p>
                        </div>
                      </div>
                      <div className="flex-1 hidden md:block md:invisible"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-10">
                口碑见证
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    quote: '小宝探花的内容总能带给我惊喜，每期视频都像一次新的冒险。那种真实感是其他达人无法比拟的。',
                    name: '小陈',
                    role: '忠实粉丝·追更2年',
                  },
                  {
                    quote: '从第一支视频追到现在，小宝探花的成长有目共睹。内容质量越来越高，但真诚的态度始终未变。',
                    name: '张先生',
                    role: '资深观众·追更3年',
                  },
                  {
                    quote: '小宝探花不只是娱乐，更是一种陪伴。低谷时期他的视频给了我很多力量，真心感谢。',
                    name: '林小姐',
                    role: '铁杆粉丝·追更1年',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="p-5 md:p-6 rounded-lg bg-background-100"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  >
                    <div className="text-accent-500 mb-3">
                      <i className="ri-double-quotes-l text-2xl"></i>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      {item.quote}
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold">
                        {item.name[0]}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                        <div className="text-xs text-foreground-500">{item.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                如有合作意向或任何问题，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@npjyf.cn' },
                  { icon: 'ri-wechat-line', title: '微信公众号', value: '小宝探花官方' },
                  { icon: 'ri-map-pin-line', title: '工作室地址', value: '中国·杭州' },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-2 p-5 rounded-lg bg-background-50 text-center"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <span className="text-xs text-foreground-500">{item.title}</span>
                    <span className="text-sm font-medium text-foreground-900">{item.value}</span>
                  </div>
                ))}
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                className="bg-background-50 rounded-lg p-6 flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">姓名 *</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">邮箱 *</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">留言内容 *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多500字</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="whitespace-nowrap self-start px-8 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600">感谢您的留言！我们会尽快回复您。</p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}